import { cosmeticSlotOf, EQUIP_SLOTS, } from '../data/items.js';
import { VISIBLE_SLOTS } from './appearance.js';
import { roundDps } from './dps.js';
import { affixStatBonus } from './affixes.js';
import { effectiveBonus } from './upgrade.js';
/**
 * Inventory rules, pure: what goes where, who may wear what, what it adds.
 * The server's InventoryService applies these inside a transaction; the
 * client uses them to grey out what a drag would refuse.
 */
export const BAG_SIZE = 30;
/**
 * The most slots a bag can ever have: the ceiling the messages validate
 * against. A development server may open an admin a bigger bag than
 * `BAG_SIZE` to hold every item at once (`DEV_BAG_SIZE`); players never get one.
 */
export const MAX_BAG_SIZE = 256;
/**
 * The wardrobe (ADR 0072): cosmetics and companions, what the shop sells,
 * kept apart from the bag and never counted against it. Its rows keep a
 * `bag_slot`, from this number up, so everything that reads « in the bag »
 * (trade, auction, refunds) still finds them, while the 30 bag slots below
 * never see them. It has no end: each newcomer takes the slot after the last.
 */
export const WARDROBE_FIRST_SLOT = 10_000;
export function isWardrobeSlot(slot) {
    return slot >= WARDROBE_FIRST_SLOT;
}
/** Whether a template lives in the wardrobe: a look (ADR 0031) or a companion (ADR 0007). */
export function belongsInWardrobe(template) {
    return template.cosmetic || template.slot === 'pet' || template.slot === 'mount';
}
/** The wardrobe slot after the last one taken. */
export function nextWardrobeSlot(bag) {
    let next = WARDROBE_FIRST_SLOT;
    for (const entry of bag)
        if (entry.slot >= next)
            next = entry.slot + 1;
    return next;
}
/** Everything its owner carries unworn, bag and wardrobe together. */
export function carriedEntries(inventory) {
    return [...inventory.bag, ...(inventory.wardrobe ?? [])];
}
/**
 * Where `quantity` of a template goes in a bag: existing stacks first, then
 * free slots in order. Null when it does not all fit — a partial grant would
 * lose loot silently. A wardrobe template (ADR 0072) always fits: `bag` must
 * then hold the wardrobe's entries too, so its slots follow the last one.
 */
export function planGrant(bag, template, quantity, bagSize = BAG_SIZE) {
    let remaining = Math.max(0, Math.floor(quantity));
    const placements = [];
    const used = new Set(bag.map((entry) => entry.slot));
    if (template.maxStack > 1) {
        for (const entry of bag) {
            if (remaining === 0)
                break;
            if (entry.item.templateId !== template.id)
                continue;
            const room = template.maxStack - entry.item.quantity;
            if (room <= 0)
                continue;
            const added = Math.min(room, remaining);
            placements.push({ slot: entry.slot, itemId: entry.item.id, quantity: added });
            remaining -= added;
        }
    }
    if (belongsInWardrobe(template)) {
        let slot = nextWardrobeSlot(bag);
        while (remaining > 0) {
            const added = Math.min(template.maxStack, remaining);
            placements.push({ slot, itemId: null, quantity: added });
            remaining -= added;
            slot += 1;
        }
        return placements;
    }
    for (let slot = 0; slot < bagSize && remaining > 0; slot += 1) {
        if (used.has(slot))
            continue;
        const added = Math.min(template.maxStack, remaining);
        placements.push({ slot, itemId: null, quantity: added });
        remaining -= added;
    }
    return remaining === 0 ? placements : null;
}
/**
 * Where one row goes: the first free bag slot, or for a wardrobe template
 * the wardrobe's next one (`bag` then holds the wardrobe's entries too).
 */
export function firstFreeSlot(bag, bagSize = BAG_SIZE, template) {
    if (template !== undefined && belongsInWardrobe(template))
        return nextWardrobeSlot(bag);
    const used = new Set(bag.map((entry) => entry.slot));
    for (let slot = 0; slot < bagSize; slot += 1)
        if (!used.has(slot))
            return slot;
    return null;
}
/** Whether a character may wear this template at all. */
export function canEquip(template, wearer) {
    if (template.kind !== 'equipment' || template.slot === undefined) {
        return { ok: false, reason: 'not-equipment' };
    }
    if (wearer.level < template.levelRequired)
        return { ok: false, reason: 'level-too-low' };
    if (template.classes.length > 0 && !template.classes.includes(wearer.classId)) {
        return { ok: false, reason: 'wrong-class' };
    }
    return { ok: true };
}
/**
 * Sum of what is worn, each piece at its effective stats (ADR 0013: the
 * template's times its upgrade bonus, rounded stat by stat before summing,
 * so a piece shows the same number in its tooltip and in the total), plus
 * its dps, health and defence affixes (ADR 0057).
 */
export function equipmentBonus(worn) {
    const total = { health: 0, defence: 0, dps: 0 };
    for (const { template, upgradeLevel, affixes } of worn) {
        const bonus = effectiveBonus(template.bonus, upgradeLevel);
        const extra = affixStatBonus(affixes);
        total.health += bonus.health + extra.health;
        total.defence += bonus.defence + extra.defence;
        total.dps = roundDps(total.dps + bonus.dps + extra.dps);
    }
    return total;
}
/**
 * What is worn, paired with its templates; an unknown template is skipped.
 * The equipment places only: a cosmetic (ADR 0031) is a look, and gives
 * nothing whatever its template says.
 */
export function wornPieces(equipped, templateOf) {
    const pieces = [];
    for (const slot of EQUIP_SLOTS) {
        const instance = equipped[slot];
        if (instance === undefined)
            continue;
        const template = templateOf(instance.templateId);
        if (template !== undefined) {
            pieces.push({ template, upgradeLevel: instance.upgradeLevel, affixes: instance.affixes });
        }
    }
    return pieces;
}
export function appearanceOf(equipped, templateOf) {
    const appearance = {};
    for (const slot of VISIBLE_SLOTS) {
        // The cosmetic over the slot is what shows (ADR 0031); the real piece
        // shows when there is none, and still fights underneath either way.
        const cosmeticSlot = cosmeticSlotOf(slot);
        const cosmetic = cosmeticSlot === undefined ? undefined : equipped[cosmeticSlot];
        // Wings (ADR 0053) have no equipment place: only their cosmetic shows.
        const instance = cosmetic ?? (slot === 'wings' ? undefined : equipped[slot]);
        if (instance === undefined)
            continue;
        const template = templateOf(instance.templateId);
        if (template?.sprite !== undefined)
            appearance[slot] = template.id;
    }
    const trail = equipped['look-trail'];
    const trailTemplate = trail === undefined ? undefined : templateOf(trail.templateId);
    if (trailTemplate?.trail !== undefined)
        appearance.trail = trailTemplate.id;
    return appearance;
}
/**
 * Upgrade level of each visible piece worn, for the paper doll's glow and
 * saturation (ADR 0013); only the levels above +0. Public like the template
 * ids beside it: everyone sees how far a sword was taken.
 */
export function upgradeLevelsOf(equipped) {
    const levels = {};
    for (const slot of VISIBLE_SLOTS) {
        // A cosmetic is never upgraded, and wings are nothing else.
        if (slot === 'wings')
            continue;
        const level = equipped[slot]?.upgradeLevel ?? 0;
        if (level > 0)
            levels[slot] = level;
    }
    return levels;
}
/** The slots that count towards the aura: every piece of equipment, companions aside. */
export const AURA_SLOTS = [
    'helmet',
    'chest',
    'gloves',
    'boots',
    'weapon',
    'offhand',
];
/**
 * The level of a character's halo (ADR 0013): the average upgrade level of
 * the six equipment slots, rounded down, an empty slot counting +0. One
 * sword at +10 alone averages +1 and lights nothing; the whole outfit at +10
 * lights the brightest halo. Public like the levels beside it.
 */
export function auraLevel(equipped) {
    return Math.floor(upgradeAverage(equipped));
}
/** The exact average behind the aura, for the inventory to show how close the next level is. */
export function upgradeAverage(equipped) {
    const total = AURA_SLOTS.reduce((sum, slot) => sum + (equipped[slot]?.upgradeLevel ?? 0), 0);
    return total / AURA_SLOTS.length;
}
//# sourceMappingURL=inventory.js.map